/* eslint-disable react/prop-types */
import React, { useState } from "react";
import Styled from "styled-components";
import { X, Package } from "@phosphor-icons/react";
import { addDays, format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { pedidosApi } from "../../page/OrderResumePag/_services/api";

const Overlay = Styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

const ModalCard = Styled.div`
  background: white;
  border-radius: 24px 24px 0 0;
  padding: 24px;
  width: 100%;
  max-width: 480px;
  max-height: 80vh;
  overflow-y: auto;
`;

const ModalHeader = Styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const ModalTitle = Styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #1D1D1F;
  margin: 0;
  font-family: var(--font-family);
`;

const CloseButton = Styled.button`
  width: 36px;
  height: 36px;
  border-radius: 100px;
  background: #F5F5F7;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const InputLabel = Styled.label`
  font-size: 14px;
  font-weight: 400;
  color: #1D1D1F;
  display: block;
  margin-bottom: 8px;
  font-family: var(--font-family);
`;

const CepInput = Styled.input`
  width: 100%;
  border: none;
  border-bottom: 0.75px solid ${(props) => (props.hasError ? "#FA5757" : "#1D1D1F")};
  outline: none;
  font-size: 16px;
  color: ${(props) => (props.hasError ? "#FA5757" : "#1D1D1F")};
  padding: 8px 0;
  font-family: var(--font-family);
  background: transparent;
`;

const ErrorText = Styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #FA5757;
  margin-top: 8px;
  margin-bottom: 0;
`;

const PrimaryButton = Styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  border-radius: 9px;
  background-color: var(--button-primary-color, #1D1D1F);
  color: var(--button-secondary-color, #FFFFFF);
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  margin-top: 24px;
  font-family: var(--font-family);

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SecondaryButton = Styled.button`
  width: 100%;
  padding: 14px;
  border: none;
  background: none;
  color: #1D1D1F;
  font-size: 14px;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  text-align: center;
  font-family: var(--font-family);
`;

const ResultBox = Styled.div`
  background-color: #F5F5F7;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResultBoxError = Styled.div`
  background-color: #FDEBEC;
  border-radius: 8px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ResultInfoRow = Styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ResultInfoText = Styled.span`
  font-size: 16px;
  font-weight: 700;
  color: #1D1D1F;
  line-height: 1.5;
  flex: 1;
`;

const ResultAddress = Styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #1D1D1F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0;
`;

const formatCep = (value) => {
  const digits = value.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 5) return `${digits.slice(0, 2)}.${digits.slice(2)}`;
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}-${digits.slice(5)}`;
};

export default function CepModal({ show, onClose, onCepConfirm, sku, userUuid, displayMode }) {
  const [cepInput, setCepInput] = useState("");
  const [modalState, setModalState] = useState("input");
  const [resultData, setResultData] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");

  const resetModal = () => {
    setCepInput("");
    setModalState("input");
    setErrorMessage("");
    setResultData(null);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleCepChange = (e) => {
    setCepInput(formatCep(e.target.value));
  };

  const handleSearch = async () => {
    const cleanCep = cepInput.replace(/\D/g, "");

    if (cleanCep.length !== 8) {
      setModalState("cep-error");
      setErrorMessage("CEP não encontrado.");
      return;
    }

    setModalState("loading");

    try {
      // 1. Validate with ViaCEP
      const viaCepResponse = await fetch(
        `https://viacep.com.br/ws/${cleanCep}/json`
      );
      const viaCepData = await viaCepResponse.json();

      if (viaCepData.erro) {
        setModalState("cep-error");
        setErrorMessage("CEP não encontrado.");
        return;
      }

      const address = `${viaCepData.logradouro} - ${viaCepData.bairro} - ${viaCepData.localidade} - ${viaCepData.uf}`;

      // 2. Calculate freight via cart API
      const channel = process.env.REACT_APP_DEFAULT_CHANNEL;
      const configData = JSON.parse(localStorage.getItem("config") || "{}");
      const fatorFrete = configData[`fator_${channel}_frete`] / 10000 || 0;

      const response = await pedidosApi.post(`v2/carrinho/${channel}`, {
        customerUuid: userUuid,
        cep: cleanCep,
        sku,
        quantity: 1,
        fator: fatorFrete,
      });

      const items = response.data.data.items;
      const firstItemKey = Object.keys(items)[0];
      const deliveryOptions = items[firstItemKey]?.deliveryOptions;

      if (
        !deliveryOptions ||
        deliveryOptions.shippingPrice === null ||
        deliveryOptions.shippingPrice === undefined
      ) {
        setResultData({ address });
        setModalState("no-delivery");
        return;
      }

      setResultData({
        price: deliveryOptions.shippingPrice,
        points: deliveryOptions.shippingPoints,
        estimatedDays: deliveryOptions.estimatedBusinessDays,
        address,
      });
      setModalState("result");
    } catch (error) {
      setModalState("cep-error");
      setErrorMessage("CEP não encontrado.");
    }
  };

  const handleConfirm = () => {
    const cleanCep = cepInput.replace(/\D/g, "");
    onCepConfirm(cleanCep, resultData);
    handleClose();
  };

  if (!show) return null;

  const renderContent = () => {
    switch (modalState) {
      case "input":
        return (
          <>
            <InputLabel>Digite seu CEP</InputLabel>
            <CepInput
              value={cepInput}
              onChange={handleCepChange}
              inputMode="numeric"
              placeholder=""
              hasError={false}
            />
            <PrimaryButton onClick={handleSearch}>BUSCAR</PrimaryButton>
          </>
        );

      case "loading":
        return (
          <>
            <InputLabel>Digite seu CEP</InputLabel>
            <CepInput
              value={cepInput}
              onChange={handleCepChange}
              inputMode="numeric"
              hasError={false}
              disabled
            />
            <PrimaryButton disabled>Calculando...</PrimaryButton>
          </>
        );

      case "cep-error":
        return (
          <>
            <InputLabel>Digite seu CEP</InputLabel>
            <CepInput
              value={cepInput}
              onChange={(e) => {
                handleCepChange(e);
                setModalState("input");
                setErrorMessage("");
              }}
              inputMode="numeric"
              hasError
            />
            <ErrorText>{errorMessage}</ErrorText>
            <PrimaryButton onClick={handleSearch}>
              BUSCAR
            </PrimaryButton>
          </>
        );

      case "result": {
        const estimatedDate = addDays(
          new Date(),
          resultData.estimatedDays || 0
        );
        const formattedDate = format(estimatedDate, "d 'de' MMM", {
          locale: ptBR,
        });
        const formatValue = () => {
          const money = resultData.price || 0;
          const points = resultData.points || 0;
          const moneyFmt = `R$ ${money.toFixed(2).replace(".", ",")}`;
          const pointsFmt = `${Math.floor(points).toLocaleString("pt-BR")} pts`;
          if (displayMode === "points") return pointsFmt;
          if (displayMode === "both") return `${moneyFmt} / ${pointsFmt}`;
          return moneyFmt;
        };

        return (
          <>
            <ResultBox>
              <ResultInfoRow>
                <Package size={20} color="#00003C" weight="regular" />
                <ResultInfoText>
                  Chega até dia {formattedDate} • {formatValue()}
                </ResultInfoText>
              </ResultInfoRow>
              <ResultAddress>{resultData.address}</ResultAddress>
            </ResultBox>
            <PrimaryButton onClick={handleConfirm}>
              USAR ESTE CEP
            </PrimaryButton>
            <SecondaryButton onClick={resetModal}>
              INFORMAR OUTRO CEP
            </SecondaryButton>
          </>
        );
      }

      case "no-delivery":
        return (
          <>
            <ResultBoxError>
              <ResultInfoRow>
                <Package size={20} color="#00003C" weight="regular" />
                <ResultInfoText>
                  Não realizamos entrega no CEP informado.
                </ResultInfoText>
              </ResultInfoRow>
              <ResultAddress>{resultData.address}</ResultAddress>
            </ResultBoxError>
            <PrimaryButton onClick={resetModal}>
              INFORMAR OUTRO CEP
            </PrimaryButton>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <Overlay onClick={handleClose}>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>Consultar frete</ModalTitle>
          <CloseButton onClick={handleClose}>
            <X size={16} color="#1D1D1F" />
          </CloseButton>
        </ModalHeader>
        {renderContent()}
      </ModalCard>
    </Overlay>
  );
}
